import React, { useState } from 'react';
import { ActivityIndicator, Alert, Dimensions, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { apiService } from '../services/apiService';

interface ProductHeaderProps {
  name: string;
  currency: string;
  bid: number;
  ask: number;
  valor: string;
  isin: string;
  termSheet?: string | null;
  onExportPdf?: () => Promise<void>;
  isExportingPdf?: boolean;
  showDocumentActions?: boolean;
}

export const ProductHeader: React.FC<ProductHeaderProps> = ({
  name,
  currency,
  bid,
  ask,
  valor,
  isin,
  termSheet,
  onExportPdf,
  isExportingPdf = false,
  showDocumentActions = true,
}) => {
  const { width } = Dimensions.get('window');
  const isDesktop = width >= 1024;
  const [isDownloading, setIsDownloading] = useState(false);
  
  const handleDownload = async () => {
    if (isDownloading) {
      return;
    }

    try {
      setIsDownloading(true);
      await apiService.downloadTermSheet(isin, termSheet || undefined);
      // Success is handled by the download itself
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error al descargar el archivo';
      
      // Show error message - use window.alert for web, Alert.alert for mobile
      if (Platform.OS === 'web') {
        window.alert(`Error: ${errorMessage}`);
      } else {
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!onExportPdf || isExportingPdf) {
      return;
    }

    try {
      await onExportPdf();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error downloading the factsheet';
      if (Platform.OS === 'web') {
        window.alert(`Error: ${errorMessage}`);
      } else {
        Alert.alert('Error', errorMessage);
      }
    }
  };

  return (
    <View style={styles.container}>
      {showDocumentActions && (
        <>
          <TouchableOpacity
            style={[styles.documentButton, isDownloading && styles.documentButtonDisabled]}
            activeOpacity={0.7}
            onPress={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <ActivityIndicator size="small" color="#374151" />
            ) : (
              <Text style={styles.documentText}>Download Termsheet</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.exportPdfButton, isExportingPdf && styles.documentButtonDisabled]}
            activeOpacity={0.7}
            onPress={handleExportPdf}
            disabled={isExportingPdf}
          >
            {isExportingPdf ? (
              <ActivityIndicator size="small" color="#374151" />
            ) : (
              <Text style={styles.documentText}>Download Product Factsheet</Text>
            )}
          </TouchableOpacity>
        </>
      )}

      {/* Product Name & Currency */}
      <View style={[styles.nameSection, !showDocumentActions && styles.nameSectionCompact]}>
        {isDesktop ? (
          <Text style={styles.productName}>
            {name} <Text style={styles.productCurrency}>{currency}</Text>
          </Text>
        ) : (
          <>
            <Text style={styles.productName}>{name}</Text>
            <Text style={styles.productCurrency}>{currency}</Text>
          </>
        )}
      </View>

      {/* Bid/Ask */}
      <View style={styles.bidAskSection}>
        <View style={styles.bidAskItem}>
          <Text style={styles.bidAskLabel}>Bid</Text>
          <Text style={styles.bidAskValue}>{bid.toFixed(2)}</Text>
        </View>
        <View style={styles.bidAskItem}>
          <Text style={styles.bidAskLabel}>Ask</Text>
          <Text style={styles.bidAskValue}>{ask.toFixed(2)}</Text>
        </View>
      </View>

      {/* Valor / ISIN */}
      <View style={styles.infoRow}>
        <View style={styles.infoItem}>
          <Text style={styles.infoLabel}>Valor</Text>
          <Text style={styles.infoValue}>{valor}</Text>
        </View>
        <View style={styles.infoItem}>
          <Text style={styles.infoLabel}>ISIN</Text>
          <Text style={styles.infoValue}>{isin}</Text>
        </View>
      </View>
    </View>
  );
};


const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
    position: 'relative',
  },
  nameSection: {
    marginBottom: 12,
    paddingRight: 120,
  },
  nameSectionCompact: {
    paddingRight: 0,
  },
  productName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#212121',
    marginBottom: 4,
  },
  productCurrency: {
    fontSize: 14,
    color: '#2196F3',
    fontWeight: '600',
  },
  bidAskSection: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 12,
  },
  bidAskItem: {
    flex: 1,
  },
  bidAskLabel: {
    fontSize: 15,
    color: '#757575',
    marginBottom: 4,
  },
  bidAskValue: {
    fontSize: 24,
    fontWeight: '600',
    color: '#212121',
  },
  infoRow: {
    flexDirection: 'row',
    gap: 20,
  },
  infoItem: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 15,
    color: '#757575',
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#212121',
  },
  documentButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    zIndex: 10,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  exportPdfButton: {
    position: 'absolute',
    top: 46,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    zIndex: 10,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  documentButtonDisabled: {
    opacity: 0.5,
  },
  documentText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
});
